import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Inbox, UserPlus, ExternalLink } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatDateTime, isAdminRole } from '../lib/utils';

export type InquiryQuality = 'unclear' | 'qualified' | 'not_qualified' | 'haldus';
export type InquiryStatus = 'uus' | 'pakkumine_saadetud' | 'sai_kliendiks' | 'ei_saanud';

export interface Inquiry {
  id: string;
  email: string;
  companyName: string;
  arrivedAt: string;
  quality: InquiryQuality;
  category: string;
  utmSource: string;
  assigneeId: string | null;
  status: InquiryStatus;
  note: string;
  value: number | null;
  clientId: string | null;
}

export const INQUIRY_CATEGORIES = [
  'e-pood päring',
  'kodulehe päring',
  'kodulehe hange',
  'disain lahendus',
  'SEO',
  'UI/UX arendus',
  'API liidestus',
  'video/pilt päring',
  'kodulehe haldus',
  'logo',
  'laravel',
  'mobiilirakendus',
  '3d modelleerimine',
  'turundus',
];

export const QUALITY_CONFIG: Record<InquiryQuality, { label: string; cls: string }> = {
  qualified: { label: 'Qualified', cls: 'bg-green-100 text-green-700' },
  unclear: { label: 'Unclear', cls: 'bg-yellow-100 text-yellow-700' },
  not_qualified: { label: 'Not qualified', cls: 'bg-red-100 text-red-700' },
  haldus: { label: 'Haldus', cls: 'bg-purple-100 text-purple-700' },
};

export const STATUS_CONFIG: Record<InquiryStatus, { label: string; cls: string }> = {
  uus: { label: 'Uus', cls: 'bg-gray-100 text-gray-600' },
  pakkumine_saadetud: { label: 'Pakkumine saadetud', cls: 'bg-blue-100 text-blue-700' },
  sai_kliendiks: { label: 'Sai kliendiks', cls: 'bg-green-100 text-green-700' },
  ei_saanud: { label: 'Ei saanud', cls: 'bg-red-100 text-red-600' },
};

const todayStr = () => new Date().toISOString().split('T')[0];

// Pill-styled inline select (saves on change).
function PillSelect<T extends string>({
  value,
  options,
  onChange,
  cls,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  cls: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={`text-xs font-medium rounded-full pl-2 pr-5 py-0.5 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 appearance-none bg-[length:12px] bg-[right_0.25rem_center] bg-no-repeat ${cls}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-white text-gray-700">{o.label}</option>
      ))}
    </select>
  );
}

function InlineText({
  value,
  onSave,
  placeholder,
  className = '',
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  if (editing) {
    return (
      <input
        autoFocus
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); if (local.trim() !== value) onSave(local.trim()); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(false); }}
        className={`w-full px-1.5 py-0.5 text-xs border border-brand-400 rounded focus:outline-none ${className}`}
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(value); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-xs text-left rounded hover:bg-brand-50 truncate ${value ? 'text-gray-700' : 'text-gray-300 italic'} ${className}`}
      title={value || 'Muuda'}
    >
      {value || placeholder || '—'}
    </button>
  );
}

export default function Inquiries() {
  const navigate = useNavigate();
  const currentUser = useStore((s) => s.currentUser);
  const users = useStore((s) => s.users);
  const projects = useStore((s) => s.projects);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InquiryStatus>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [importStatus, setImportStatus] = useState<{ lastRun: string | null; fileUpdatedAt: string | null; summary: { added: number; skipped: number; needsReview: number; classifier: string } | null } | null>(null);
  const [importing, setImporting] = useState(false);

  // Quick-add form
  const [qaEmail, setQaEmail] = useState('');
  const [qaDate, setQaDate] = useState(todayStr());
  const [qaQuality, setQaQuality] = useState<InquiryQuality>('unclear');
  const [qaCategory, setQaCategory] = useState('');
  const [qaAssignee, setQaAssignee] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const r = await apiClient.get<{ inquiries: Inquiry[] }>('/inquiries');
      setInquiries(r.inquiries);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const loadImportStatus = useCallback(() => {
    apiClient.get<typeof importStatus>('/inquiries/import/status').then(setImportStatus).catch(() => undefined);
  }, []);
  useEffect(() => { loadImportStatus(); }, [loadImportStatus]);

  const runImport = async () => {
    setImporting(true);
    try {
      await apiClient.post('/inquiries/import', {});
      await load();
      loadImportStatus();
    } catch (e) {
      useStore.getState().addError('importInquiries', e);
    } finally {
      setImporting(false);
    }
  };

  const canUse = currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role);

  // Assignees: salespeople only — anyone who owns a project as sales owner,
  // plus anyone already assigned on an inquiry (so old rows keep their name).
  // The seed "Admin" service account is excluded.
  const assignees = useMemo(() => {
    const ids = new Set(projects.map((pr) => pr.salesOwnerId));
    for (const i of inquiries) if (i.assigneeId) ids.add(i.assigneeId);
    return users
      .filter((u) => ids.has(u.id) && !(u.name === 'Admin' || u.email === 'admin@vdisain.ee'))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, inquiries, users]);
  const userName = (id: string | null) => (id ? assignees.find((u) => u.id === id)?.name ?? '?' : null);

  // Lead sources present in the data, for the filter dropdown.
  const sources = useMemo(
    () => Array.from(new Set(inquiries.map((i) => i.utmSource).filter(Boolean))).sort(),
    [inquiries],
  );

  const filtered = useMemo(
    () => inquiries.filter((i) => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (sourceFilter === 'all') return true;
      if (sourceFilter === '(teadmata)') return !i.utmSource;
      return i.utmSource === sourceFilter;
    }),
    [inquiries, statusFilter, sourceFilter],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: inquiries.length };
    for (const s of Object.keys(STATUS_CONFIG)) c[s] = inquiries.filter((i) => i.status === s).length;
    return c;
  }, [inquiries]);

  if (!canUse) {
    return <div className="p-8 text-center text-gray-400">This page is available to admins and salespeople only.</div>;
  }

  const patch = async (id: string, updates: Record<string, unknown>) => {
    try {
      await apiClient.patch(`/inquiries/${id}`, updates);
      await load();
    } catch (e) {
      useStore.getState().addError('updateInquiry', e);
    }
  };

  const quickAdd = async () => {
    if (!qaEmail.trim() || adding) return;
    setAdding(true);
    try {
      await apiClient.post('/inquiries', {
        email: qaEmail.trim(),
        arrivedAt: qaDate,
        quality: qaQuality,
        category: qaCategory,
        assigneeId: qaAssignee || null,
      });
      setQaEmail('');
      setQaCategory('');
      await load();
    } catch (e) {
      useStore.getState().addError('addInquiry', e);
    } finally {
      setAdding(false);
    }
  };

  const removeRow = async (id: string) => {
    try {
      await apiClient.delete(`/inquiries/${id}`);
      await load();
    } catch (e) {
      useStore.getState().addError('deleteInquiry', e);
    }
  };

  const convert = async (inq: Inquiry) => {
    try {
      const r = await apiClient.post<{ client: { id: string } }>(`/inquiries/${inq.id}/convert`, {});
      await load();
      // Jump straight into project creation with the new client preselected —
      // the project will auto-log itself on the Müük ledger.
      navigate('/projects', { state: { newProjectClientId: r.client.id } });
    } catch (e) {
      useStore.getState().addError('convertInquiry', e);
    }
  };

  const selectCls = 'text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 text-gray-700 bg-white';

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Müük · Päringud</h1>
        <p className="text-gray-500 text-sm mt-1">Sissetulevad päringud · {inquiries.length} kokku</p>
      </div>

      {/* Nightly contact-form import */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3 px-1">
        <p className="text-[11px] text-gray-400">
          Automaatne import kontaktivormist igal hommikul kell 04:00
          {importStatus?.lastRun && <> · viimati {formatDateTime(importStatus.lastRun)}</>}
          {importStatus?.summary && <> · lisatud {importStatus.summary.added}, vahele jäetud {importStatus.summary.skipped}
            {importStatus.summary.needsReview > 0 && <>, ülevaatamist vajab {importStatus.summary.needsReview}</>}</>}
        </p>
        {isAdminRole(currentUser?.role) && (
          <button
            onClick={runImport}
            disabled={importing}
            className="text-[11px] px-2.5 py-1 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            {importing ? 'Impordin…' : 'Impordi kohe'}
          </button>
        )}
      </div>

      {/* Quick add */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={qaEmail}
            onChange={(e) => setQaEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') quickAdd(); }}
            placeholder="email@ettevote.ee *"
            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            autoFocus
          />
          <input type="date" value={qaDate} onChange={(e) => setQaDate(e.target.value)} className={selectCls} />
          <select value={qaQuality} onChange={(e) => setQaQuality(e.target.value as InquiryQuality)} className={selectCls}>
            {Object.entries(QUALITY_CONFIG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
          </select>
          <select value={qaCategory} onChange={(e) => setQaCategory(e.target.value)} className={selectCls}>
            <option value="">— kategooria</option>
            {INQUIRY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={qaAssignee} onChange={(e) => setQaAssignee(e.target.value)} className={selectCls}>
            <option value="">— kes sai endale?</option>
            {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button
            onClick={quickAdd}
            disabled={!qaEmail.trim() || adding}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 disabled:opacity-50"
          >
            <Plus size={14} /> Lisa
          </button>
        </div>
        <p className="text-[11px] text-gray-300 mt-2">Enter emaili väljal lisab kohe · ülejäänu saab täita hiljem tabelis</p>
      </div>

      {/* Status filter */}
      <div className="flex gap-1.5 flex-wrap mb-4">
        {(['all', ...Object.keys(STATUS_CONFIG)] as ('all' | InquiryStatus)[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
              statusFilter === s ? 'border-brand-300 bg-brand-50 text-brand-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'Kõik' : STATUS_CONFIG[s as InquiryStatus].label} ({counts[s] ?? 0})
          </button>
        ))}
        {/* Where the lead came from (UTM source on the website form). */}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
          title="Filtreeri allika järgi"
        >
          <option value="all">Kõik allikad</option>
          {sources.map((src) => (
            <option key={src} value={src}>{src} ({inquiries.filter((i) => i.utmSource === src).length})</option>
          ))}
          <option value="(teadmata)">(teadmata) ({inquiries.filter((i) => !i.utmSource).length})</option>
        </select>
      </div>

      {error && <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-4">{error}</div>}
      {loading && <div className="p-12 text-center text-gray-400 text-sm">Laen…</div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Inbox size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-sm">Päringuid pole. Lisa esimene ülevalt — ainult email ja Enter.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Saabus', 'Email / ettevõte', 'Allikas', 'Kvaliteet', 'Kes sai?', 'Mida soovib?', 'Staatus', 'Märkus / miks ei tulnud?', 'Väärtus', ''].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((inq) => (
                  <tr key={inq.id} className={`group ${inq.status === 'sai_kliendiks' ? 'bg-green-50/40' : inq.status === 'ei_saanud' ? 'bg-red-50/20' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        type="date"
                        value={inq.arrivedAt.split('T')[0]}
                        onChange={(e) => e.target.value && patch(inq.id, { arrivedAt: e.target.value })}
                        className="text-xs text-gray-600 border-0 bg-transparent rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-400 w-[105px]"
                      />
                    </td>
                    <td className="px-3 py-2 min-w-[180px] max-w-[240px]">
                      <InlineText value={inq.email} onSave={(v) => v && patch(inq.id, { email: v })} className="font-medium" />
                      <InlineText value={inq.companyName} onSave={(v) => patch(inq.id, { companyName: v })} placeholder="ettevõtte nimi…" />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <InlineText
                        value={inq.utmSource}
                        onSave={(v) => patch(inq.id, { utmSource: v })}
                        placeholder="—"
                        className={inq.utmSource ? 'text-gray-600' : 'text-gray-300'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <PillSelect
                        value={inq.quality}
                        options={Object.entries(QUALITY_CONFIG).map(([v, c]) => ({ value: v as InquiryQuality, label: c.label }))}
                        onChange={(v) => patch(inq.id, { quality: v })}
                        cls={QUALITY_CONFIG[inq.quality].cls}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={inq.assigneeId ?? ''}
                        onChange={(e) => patch(inq.id, { assigneeId: e.target.value || null })}
                        className={`text-xs border-0 bg-transparent rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-400 max-w-[120px] ${inq.assigneeId ? 'text-gray-700' : 'text-gray-300'}`}
                      >
                        <option value="">—</option>
                        {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={inq.category}
                        onChange={(e) => patch(inq.id, { category: e.target.value })}
                        className={`text-xs border-0 bg-transparent rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-400 max-w-[150px] ${inq.category ? 'text-gray-700' : 'text-gray-300'}`}
                      >
                        <option value="">— vali</option>
                        {INQUIRY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <PillSelect
                        value={inq.status}
                        options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({ value: v as InquiryStatus, label: c.label }))}
                        onChange={(v) => patch(inq.id, { status: v })}
                        cls={STATUS_CONFIG[inq.status].cls}
                      />
                    </td>
                    <td className="px-3 py-2 min-w-[160px] max-w-[220px]">
                      <InlineText value={inq.note} onSave={(v) => patch(inq.id, { note: v })} placeholder="märkus…" />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap w-[90px]">
                      <InlineText
                        value={inq.value != null ? String(inq.value) : ''}
                        onSave={(v) => patch(inq.id, { value: v.trim() === '' ? null : Number(v) })}
                        placeholder="€"
                        className="text-right"
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1 justify-end">
                        {inq.clientId ? (
                          <button
                            onClick={() => navigate(`/clients/${inq.clientId}`)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-100 rounded-lg hover:bg-green-200 font-medium"
                            title="Ava klient"
                          >
                            <ExternalLink size={11} /> Klient
                          </button>
                        ) : (
                          <button
                            onClick={() => convert(inq)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 border border-brand-200 rounded-lg font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Loo klient ja alusta projekti"
                          >
                            <UserPlus size={11} /> Tee kliendiks
                          </button>
                        )}
                        <button
                          onClick={() => removeRow(inq.id)}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Kustuta"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3">
        „Tee kliendiks" loob kliendikirje, märgib päringu võidetuks ja avab kohe uue projekti vormi — projekt logib end ise Müügi lehele.
        Väärtus (€) täida võidetud päringutel — Statistika kasutab seda võidetud käibe arvutuses.
      </p>
    </div>
  );
}
